import { Redirect, Route, Router, Switch } from 'react-router-dom';
import React, { lazy, Suspense, useEffect, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone';
import 'moment/locale/he';
import 'moment/locale/ja';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { ILoggedUser } from '@shared/interfaces/IUser';
import { ILoginRequestStatus, LoginRequestStatuses } from '@shared/interfaces/ILoginReducerState';
import { useTabVisibility } from '@shared/hooks/useTabVisibility.hook';
import { Environments } from '@shared/constants/environments';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { QueryClient } from '@tanstack/react-query';
import { IUserPreferences } from '@shared/interfaces/IUserPreferences';
import * as Sentry from '@sentry/react';
import { MonitoringRoute } from '@shared/utils/monitoring.util';
import { customHistory } from '@shared/utils/history.utils';
import { translationService } from '@src/servicesInitializer';
import { IRootState } from './store/slices';
import { selectProject } from './store/slices/project.slice';
import { AppRoutes } from './constants/appRoutes.enum';
import { appBecameVisible_BI } from './utils/bi.utils';
import { TrusstorToastMessages } from './components/TrusstorToastMessages/TrusstorToastMessages';
import { SplashScreen } from './components/SplashScreen/SplashScreen';
import { setIsOffline } from './store/slices/offlineBanner.slice';
import { errorToast } from './utils/toast.utils';
import { ErrorPage } from './components/pages/ErrorPage/ErrorPage';
import { config } from './config/config';

// The public shell / authenticated app boundary, mirroring the desktop app. The entry chunk
// carries only this shell; every page, the drawers and the logged-in side effects live in the
// lazy AuthenticatedApp chunk and never load before a session exists.
const AuthenticatedApp = lazy(() =>
	import('./components/AuthenticatedApp/AuthenticatedApp').then((m) => ({ default: m.AuthenticatedApp }))
);

// Lazy on purpose. These three are the only screens that pull in the Better Auth client, and it
// must not sit in the eager entry chunk: in a flag-off environment it can never run, and even
// with the flag on it is dead weight for the logged-in app. Statically importing them here would
// also defeat the dynamic `import()` the login thunk and the bootstrap use for the same reason.
const LoginPage = lazy(() =>
	import('./components/pages/AuthPages/LoginPage/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const TwoFactorChallengePage = lazy(() =>
	import('./components/pages/AuthPages/TwoFactorChallengePage/TwoFactorChallengePage').then((m) => ({
		default: m.TwoFactorChallengePage,
	}))
);
const TwoFactorEnrollmentPage = lazy(() =>
	import('./components/pages/AuthPages/TwoFactorEnrollmentPage/TwoFactorEnrollmentPage').then((m) => ({
		default: m.TwoFactorEnrollmentPage,
	}))
);

const queryClient = new QueryClient();
const persister = createSyncStoragePersister({
	storage: window.localStorage,
});

const getThemeStyles = (): any => ({
	direction: document.body.dir,
	typography: {
		button: {
			textTransform: 'none',
		},
	},
});

function App() {
	const dispatch = useDispatch();
	document.body.dir = translationService.getDirection();
	const [areTranslationsFetched, setAreTranslationsFetched] = useState<boolean>(false);
	const [theme, setTheme] = useState(createTheme(getThemeStyles()));
	const loggedUser: ILoggedUser | null = useSelector((state: IRootState) => state.loginReducer.loggedUser);
	const loginRequestStatus: ILoginRequestStatus = useSelector(
		(state: IRootState) => state.loginReducer.loginRequestStatus
	);
	const loggedUserPreferences: IUserPreferences | null | undefined = loggedUser?.userPreferences;
	const workingProject = useSelector(selectProject);
	useTabVisibility(appBecameVisible_BI);
	const shouldShowSplashScreen: boolean =
		import.meta.env.REACT_APP_STAGE !== Environments.dev &&
		import.meta.env.REACT_APP_STAGE !== Environments.compose;
	const [showSplashScreen, setShowSplashScreen] = useState(shouldShowSplashScreen);

	useEffect(() => {
		const handleOnline = () => {
			dispatch(setIsOffline(false));
		};

		const handleOffline = () => {
			dispatch(setIsOffline(true));
		};

		window.addEventListener('online', handleOnline);
		window.addEventListener('offline', handleOffline);
		return () => {
			window.removeEventListener('online', handleOnline);
			window.removeEventListener('offline', handleOffline);
		};
	}, []);

	const updateUserLayout = () => {
		const language: string = loggedUserPreferences?.language || workingProject!.language;
		translationService.changeChosenLanguage(language);
		moment.locale(translationService.getDateLocale());
		document.body.dir = translationService.getDirection();
		setTheme(createTheme(getThemeStyles()));
	};

	useEffect(() => {
		if (loggedUserPreferences) {
			updateUserLayout();
		}
	}, [loggedUserPreferences]);

	useEffect(() => {
		if (loggedUser && workingProject) {
			updateUserLayout();
		}
	}, [loggedUser?.userDetails?.username, workingProject?.projectId, setTheme]);

	useEffect(() => {
		setShowSplashScreen(shouldShowSplashScreen);
		const doSomethingAfterDuration = () => {
			setShowSplashScreen(false);
		};

		const twoSecondsInMilliseconds: number = 2000;

		const timer = setTimeout(doSomethingAfterDuration, twoSecondsInMilliseconds);

		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		const initTranslations = async () => {
			const { error } = await translationService.init();
			setAreTranslationsFetched(true);
			if (error) {
				errorToast(dispatch, 'Error fetching translations, please try again later');
			}
		};
		if (loggedUser?.userDetails.username) {
			setAreTranslationsFetched(false);
			initTranslations();
			return;
		}
		// Logged out, the pre-login screens have to render — and they cannot while this gate
		// holds the splash screen up. The bundled translations carry them; the remote fetch
		// still happens the moment a user exists.
		setAreTranslationsFetched(true);
	}, [loggedUser?.userDetails?.username]);

	/** A live session must never see the login form — send them into the app instead. */
	const RedirectToAuthenticatedApp = () => <Redirect to={{ pathname: '/' }} />;

	if (showSplashScreen || !areTranslationsFetched) {
		return <SplashScreen />;
	}

	if (
		loginRequestStatus.state === LoginRequestStatuses.LOADING ||
		loginRequestStatus.state === LoginRequestStatuses.ERROR
	) {
		return <div />;
	}
	return (
		<PersistQueryClientProvider
			onSuccess={() => queryClient.resumePausedMutations().then(() => queryClient.invalidateQueries())}
			persistOptions={{ persister }}
			client={queryClient}
		>
			<ThemeProvider theme={theme}>
				<div className="App">
					<Sentry.ErrorBoundary fallback={<ErrorPage />}>
						<Router history={customHistory}>
							{/* The auth screens and AuthenticatedApp are all lazy, so the fallback
							    is a blank frame rather than the splash animation. */}
							<Suspense fallback={<div />}>
								<Switch>
									{/* This app's own auth screens. Pre-login by nature — no
									    ProtectedRoute, because there is no session yet: /login has
									    none at all, and the two MFA routes hold only engine state
									    (a short-lived 2FA cookie, or a session the bridge has
									    refused to exchange). */}
									{/* A logged-in user on /login belongs in the app, whatever sent them
									    here. Desktop makes the same assertion for the same reason: it now
									    redirects phone-shaped visitors to this route in EVERY environment,
									    so a user with a live mobile session who opens the desktop URL is
									    replaced to `/login` — and `location.replace` leaves no back button. */}
									<MonitoringRoute
										exact
										path={AppRoutes.login}
										component={loggedUser ? RedirectToAuthenticatedApp : LoginPage}
									/>
									<MonitoringRoute
										exact
										path={AppRoutes.twoFactor}
										component={TwoFactorChallengePage}
									/>
									<MonitoringRoute
										exact
										path={AppRoutes.twoFactorEnrollment}
										component={TwoFactorEnrollmentPage}
									/>
									<Route path="*" render={() => <AuthenticatedApp />} />
								</Switch>
							</Suspense>
						</Router>
						<TrusstorToastMessages />
					</Sentry.ErrorBoundary>
				</div>
			</ThemeProvider>
		</PersistQueryClientProvider>
	);
}

export default App;
